import {
  Alert,
  Badge,
  Box,
  Code,
  Flex,
  Heading,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useParams } from "react-router";

import { LinearCleanupCreationStatusBadge } from "@/admin/components/LinearCleanupCreationStatusBadge";
import { useAdminLinearCleanupIssue } from "@/admin/data/useAdminLinearCleanupIssues";
import { MarkdownDisplay } from "@/common/components/MarkdownDisplay";
import TimestampCell from "@/common/components/TimestampCell";
import { useSlugParam } from "@/common/hooks/useParam";
import { LinearIssueStateBadge } from "@/feature/components/LinearIssueStateBadge";
import pluralize from "@reflag/shared/utils/pluralize";

export default function AdminLinearCleanupIssue() {
  const { orgId } = useSlugParam("org");
  const { issueId } = useParams();
  const {
    data: issue,
    isLoading,
    isError,
  } = useAdminLinearCleanupIssue(orgId!, issueId!);
  if (isLoading) return <Spinner />;
  if (isError || !issue)
    return (
      <Alert status="error">Could not load this Linear cleanup attempt.</Alert>
    );

  return (
    <Box marginInline="auto" maxW="container.xl" paddingInline={4}>
      <Flex alignItems="center" as="header" gap={8} paddingBlock={4}>
        <Heading as="h1">
          <RouterLink to="/admin">Reflag admin</RouterLink>
        </Heading>
        <Text color="dimmed">/</Text>
        <Link as={RouterLink} to={`/admin/${orgId}`}>
          Organization
        </Link>
        <Text color="dimmed">/</Text>
        <Text>Linear cleanup issue</Text>
      </Flex>
      <Flex as="main" direction="column" gap={6}>
        <section>
          <Heading as="h2" fontSize="xl" mb={4}>
            {issue.title}
          </Heading>
          <Table>
            <Tbody>
              <Tr>
                <Th>Flag</Th>
                <Td>
                  {issue.feature.url ? (
                    <Link as={RouterLink} to={issue.feature.url}>
                      {issue.feature.name} ({issue.feature.key})
                    </Link>
                  ) : (
                    issue.feature.name
                  )}{" "}
                  · {issue.feature.appName}
                </Td>
              </Tr>
              <Tr>
                <Th>Creation result</Th>
                <Td>
                  <LinearCleanupCreationStatusBadge status={issue.status} />{" "}
                  {!!issue.warningCount && (
                    <Badge ml={2} colorScheme="yellow">
                      {issue.warningCount}{" "}
                      {pluralize("warning", issue.warningCount)}
                    </Badge>
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Linear issue</Th>
                <Td>
                  {issue.url ? (
                    <Link href={issue.url} isExternal>
                      {issue.details?.identifier ?? issue.url}
                    </Link>
                  ) : (
                    "Not created"
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Linear state</Th>
                <Td>
                  {issue.deletedAt ? (
                    <Badge>Deleted</Badge>
                  ) : issue.status === "created" ? (
                    <LinearIssueStateBadge
                      state={issue.details?.state ?? null}
                    />
                  ) : (
                    "—"
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Created</Th>
                <Td>
                  <TimestampCell value={issue.createdAt} />
                </Td>
              </Tr>
              <Tr>
                <Th>Updated</Th>
                <Td>
                  <TimestampCell value={issue.updatedAt} />
                </Td>
              </Tr>
              <Tr>
                <Th>Last webhook event</Th>
                <Td>
                  {issue.lastWebhookAt ? (
                    <TimestampCell value={issue.lastWebhookAt} />
                  ) : (
                    "None received"
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Linear template</Th>
                <Td>
                  {issue.templateName ?? issue.templateId ?? "Reflag text"}
                </Td>
              </Tr>
              <Tr>
                <Th>Team (Linear ID)</Th>
                <Td>{issue.teamId ?? "Not resolved"}</Td>
              </Tr>
              <Tr>
                <Th>Project (Linear ID)</Th>
                <Td>{issue.projectId ?? "None"}</Td>
              </Tr>
              <Tr>
                <Th>Reflag owner at creation</Th>
                <Td>
                  {issue.ownerEmail
                    ? `${issue.ownerName ?? ""} <${issue.ownerEmail}>`
                    : "No owner"}
                </Td>
              </Tr>
              <Tr>
                <Th>Requested assignee (Linear ID)</Th>
                <Td>{issue.assigneeId ?? "Unassigned"}</Td>
              </Tr>
              <Tr>
                <Th>Requested agent (Linear ID)</Th>
                <Td>{issue.delegateId ?? "None"}</Td>
              </Tr>
              <Tr>
                <Th>Creation / Linear UUID</Th>
                <Td>
                  <Code>{issue.id}</Code>
                </Td>
              </Tr>
            </Tbody>
          </Table>
          <Text color="dimmed" fontSize="sm" mt={3}>
            Assignment and delegation outcomes are recorded in the logs below.
          </Text>
        </section>
        <section>
          <Heading as="h2" fontSize="xl" mb={4}>
            Rendered issue description
          </Heading>
          {issue.description ? (
            <MarkdownDisplay size="sm" value={issue.description} />
          ) : (
            <Text color="dimmed">No description recorded</Text>
          )}
        </section>
        <section>
          <Heading as="h2" fontSize="xl" mb={4}>
            Logs
          </Heading>
          <VStack align="stretch" spacing={3}>
            {issue.logs.map((log) => (
              <Box key={log.id} borderWidth="1px" borderRadius="md" p={4}>
                <Flex align="center" gap={2} mb={2}>
                  <Badge
                    colorScheme={
                      log.level === "error"
                        ? "red"
                        : log.level === "warn"
                          ? "yellow"
                          : "gray"
                    }
                  >
                    {log.level}
                  </Badge>
                  <TimestampCell value={log.createdAt} />
                </Flex>
                <Text>{log.message}</Text>
                {log.metadata && (
                  <Code
                    display="block"
                    overflowX="auto"
                    whiteSpace="pre"
                    mt={2}
                  >
                    {JSON.stringify(log.metadata, null, 2)}
                  </Code>
                )}
              </Box>
            ))}
            {!issue.logs.length && (
              <Text color="dimmed">No logs recorded yet</Text>
            )}
          </VStack>
        </section>
      </Flex>
    </Box>
  );
}
